import { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { MoneyInput } from '../common/MoneyInput';
import { DatePicker } from '../common/DatePicker';
import { canonicalMoney, sumMoney, multiplyMoney, multiplyByPercent, addMoney, formatMoney } from '../../lib/money';
import type { InvoiceLineItem } from '../../types';

// Local form-state shape: unit_price is string (canonical after MoneyInput blur)
// during edit; Number(canonicalMoney()) at submit boundary for the numeric DB column.
type FormLineItem = Omit<InvoiceLineItem, 'unit_price'> & { unit_price: string };

interface CreateRecurringPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  createTemplate: (data: {
    business_id: string;
    created_by: string;
    client_name: string;
    client_email: string | null;
    line_items: InvoiceLineItem[];
    subtotal: number;
    tax_rate: number;
    tax_amount: number;
    total: number;
    frequency: string;
    next_invoice_date: string;
    is_active: boolean;
    auto_send: boolean;
    notes: string | null;
  }) => Promise<boolean>;
}

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

export function CreateRecurringPanel({ isOpen, onClose, onCreated, createTemplate }: CreateRecurringPanelProps) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);

  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [lineItems, setLineItems] = useState<FormLineItem[]>([{ description: '', quantity: 1, unit_price: '', amount: 0 }]);
  const [frequency, setFrequency] = useState('monthly');
  const [nextDate, setNextDate] = useState('');
  const [autoSend, setAutoSend] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = useCallback(() => {
    setClientName(''); setClientEmail('');
    setLineItems([{ description: '', quantity: 1, unit_price: '', amount: 0 }]);
    setFrequency('monthly');
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    setNextDate(d.toISOString().split('T')[0]);
    setAutoSend(false); setNotes('');
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { if (isOpen) reset(); }, [isOpen, reset]);

  function updateLine(idx: number, field: keyof FormLineItem, value: string | number) {
    const updated = [...lineItems];
    const item = { ...updated[idx], [field]: value };
    // Rule #34: qty × unit_price through the Money wrapper.
    item.amount = Number(multiplyMoney(canonicalMoney(item.unit_price), item.quantity));
    updated[idx] = item;
    setLineItems(updated);
  }

  function addLine() { setLineItems([...lineItems, { description: '', quantity: 1, unit_price: '', amount: 0 }]); }
  function removeLine(idx: number) { if (lineItems.length > 1) setLineItems(lineItems.filter((_, i) => i !== idx)); }

  // Rule #34: canonical money math through the aggregation chain.
  const taxRate = 0.13;
  const subtotalCanonical = sumMoney(lineItems.map(l => canonicalMoney(l.amount)));
  const taxCanonical = multiplyByPercent(subtotalCanonical, taxRate * 100);
  const totalCanonical = addMoney(subtotalCanonical, taxCanonical);

  async function handleSubmit() {
    if (!business || !user || !clientName.trim() || lineItems.every(l => !l.description.trim())) return;
    setSaving(true);
    const ok = await createTemplate({
      business_id: business.id,
      created_by: user.id,
      client_name: clientName.trim(),
      client_email: clientEmail.trim() || null,
      // Hook accepts InvoiceLineItem[] with unit_price: number; Number(canonical) at the boundary.
      line_items: lineItems.filter(l => l.description.trim()).map(li => ({
        ...li,
        unit_price: Number(canonicalMoney(li.unit_price)),
      })),
      subtotal: Number(subtotalCanonical), tax_rate: taxRate,
      tax_amount: Number(taxCanonical), total: Number(totalCanonical),
      frequency,
      next_invoice_date: nextDate,
      is_active: true,
      auto_send: autoSend,
      notes: notes.trim() || null,
    });
    setSaving(false);
    if (ok) { onCreated(); onClose(); }
  }

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: palette.g600, marginBottom: 5 };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`,
    fontSize: 13.5, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };

  return (
    <>
      {isOpen && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 40 }} />}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
        background: palette.white, zIndex: 50, borderLeft: `1px solid ${palette.g150}`,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.08)',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 200ms ease', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: `1px solid ${palette.g150}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800 }}>New Recurring Invoice</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.g400, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Client Name *</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Client name" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Client Email</label>
              <input value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="client@email.com" style={inputStyle} />
            </div>

            {/* Line items */}
            <div>
              <label style={labelStyle}>Line Items</label>
              {lineItems.map((li, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" style={{ ...inputStyle, flex: '1 1 auto', minWidth: 0 }} />
                  <input type="number" min="1" value={li.quantity} onChange={e => updateLine(i, 'quantity', parseInt(e.target.value) || 1)} style={{ ...inputStyle, flex: '0 0 64px', textAlign: 'center' }} />
                  <div style={{ flex: '0 0 120px' }}><MoneyInput value={li.unit_price} onChange={v => updateLine(i, 'unit_price', v)} /></div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: palette.g700, minWidth: 60, textAlign: 'right' }}>{formatMoney(li.amount)}</span>
                  {lineItems.length > 1 && (
                    <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.g400, padding: 2 }}><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
              <button onClick={addLine} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, color: palette.accent, fontFamily: 'inherit', fontWeight: 500,
              }}><Plus size={14} /> Add line item</button>
            </div>

            {/* Totals */}
            <div style={{ padding: '12px 0', borderTop: `1px solid ${palette.g100}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: palette.g600, marginBottom: 4 }}>
                <span>Subtotal</span><span>{formatMoney(subtotalCanonical)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: palette.g600, marginBottom: 4 }}>
                <span>HST (13%)</span><span>{formatMoney(taxCanonical)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700, color: palette.g800 }}>
                <span>Total</span><span>{formatMoney(totalCanonical)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Frequency</label>
                <select value={frequency} onChange={e => setFrequency(e.target.value)} style={inputStyle}>
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Next Invoice Date</label>
                <DatePicker
                  value={nextDate || null}
                  onChange={(v) => setNextDate(v ?? '')}
                  minDate={new Date().toISOString().split('T')[0]}
                  clearable={false}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" id="auto-send-toggle" checked={autoSend} onChange={e => setAutoSend(e.target.checked)} style={{ cursor: 'pointer' }} />
              <label htmlFor="auto-send-toggle" style={{ fontSize: 13, color: palette.g600, cursor: 'pointer' }}>Auto-send when generated</label>
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 24px', borderTop: `1px solid ${palette.g150}` }}>
          <button onClick={onClose} style={{
            padding: '8px 20px', borderRadius: 6, border: `1px solid ${palette.g200}`,
            background: palette.white, fontSize: 13, fontWeight: 600, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit',
          }}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !clientName.trim()}
            style={{
              padding: '8px 20px', borderRadius: 6, border: 'none',
              background: saving || !clientName.trim() ? palette.g300 : palette.accent,
              fontSize: 13, fontWeight: 600, color: palette.white,
              cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit',
            }}
          >{saving ? 'Creating...' : 'Create Template'}</button>
        </div>
      </div>
    </>
  );
}
